import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { SCALEDRONE_CHANNEL_ID, SCALEDRONE_TEST_MODE_MAX_CLIENTS, SCALEDRONE_TEST_CLIENT_ID } from '@/client/config';
import { useTranscodeJobsStore } from '@/client/store/transcodeJobsStore';
import { useLiveStreamsStore } from '@/client/store/liveStreamsStore';
import { useClientsStore } from '@/client/store/clientsStore';
import { useEncoderWSSStore } from '@/client/store/encoderWSSStore';
import { useAuth0 } from '@auth0/auth0-react';
import { showFlappingToast } from '@/client/components/flapping-toast';
import { SCALEDRONE_CONFIG } from '@shared/config/scaledrone';
import { isLoginRequiredError } from '@/client/lib/apiError';
import type { WebSocketEventData, ConnectionStatus, ScaledroneWorkerMessage, EncoderSampleData } from '@shared/types';

// DDOS Prevention Configuration - uses shared config
const { MAX_CONNECTION_ATTEMPTS, CIRCUIT_BREAKER_THRESHOLD, CONNECTION_TIMEOUT_MS } = SCALEDRONE_CONFIG;

interface ScaledroneContextValue {
  status: ConnectionStatus;
  error: string | null;
  subscriptionProgress: { subscribed: number; total: number } | null;
}

const ScaledroneContext = createContext<ScaledroneContextValue | null>(null);

export function useScaledroneContext() {
  const context = useContext(ScaledroneContext);
  if (!context) {
    throw new Error('useScaledroneContext must be used within a ScaledroneProvider');
  }
  return context;
}

interface ScaledroneProviderProps {
  children: React.ReactNode;
}

export function ScaledroneProvider({ children }: ScaledroneProviderProps) {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const { clients, fetchClients } = useClientsStore();

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [subscriptionProgress, setSubscriptionProgress] = useState<{ subscribed: number; total: number } | null>(null);

  // Refs for connection management
  const workerRef = useRef<Worker | null>(null);
  const droneRef = useRef<any>(null);
  const roomsRef = useRef<Map<string, any>>(new Map());
  const subscriptionInProgressRef = useRef<boolean>(false);
  const connectionAttemptsRef = useRef<number>(0);
  const circuitBreakerTrippedRef = useRef<boolean>(false);
  const isInitializingRef = useRef<boolean>(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalCloseRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  const processWorkerMessageRef = useRef<((message: ScaledroneWorkerMessage) => void) | null>(null);

  // Store actions
  const updateJobFromWSS = useTranscodeJobsStore((state) => state.updateJobFromWSS);
  const updateEventStatus = useLiveStreamsStore((state) => state.updateEventStatus);
  const processEncoderEvent = useEncoderWSSStore((state) => state.processEvent);

  // Get active client IDs
  // If SCALEDRONE_TEST_CLIENT_ID is set, only subscribe to that single client
  const activeClientIDs = useMemo(() => {
    if (SCALEDRONE_TEST_CLIENT_ID) {
      return [SCALEDRONE_TEST_CLIENT_ID];
    }
    return clients
      .filter(client => client.active === 1)
      .map(client => client.clientID);
  }, [clients]);

  // Process worker messages
  // Note: This callback is stored in a ref and accessed via the worker's onmessage handler.
  // We use getState() to access store data to avoid stale closure issues.
  const processWorkerMessage = useCallback((message: ScaledroneWorkerMessage) => {
    switch (message.type) {
      case 'event_status':
        if (message.raw && typeof message.raw === 'object') {
          updateEventStatus(message.raw as WebSocketEventData);
        }
        break;

      case 'transcode_job':
        updateJobFromWSS(
          message.jobID,
          message.assetID,
          message.eventID,
          message.clientID,
          message.progress,
          message.status,
          message.raw
        );
        break;

      case 'encoder_wss': {
        // Get previous state before processing
        const prevState = useEncoderWSSStore.getState().encoders.get(message.encoderID)?.state;

        // Process the event and get the new state
        const newState = processEncoderEvent({
          encoderID: message.encoderID,
          eventType: message.eventType,
          online: message.online,
          streamInfo: message.streamInfo as EncoderSampleData | null,
          simpleName: message.simpleName,
          clientID: message.clientID,
          timestamp: message.timestamp,
        });

        // Show toast when transitioning to warning or alert state (flapping cycles only)
        // Note: Live stream disconnects are handled server-side and delivered via SSE
        if ((newState === 'warning' || newState === 'alert') && prevState !== newState) {
          // Count cycles from encoder state
          const encoder = useEncoderWSSStore.getState().encoders.get(message.encoderID);
          const cycles = encoder?.events.reduce((count, event, i, arr) => {
            if (i === 0) return count;
            if (arr[i - 1].type === 'disconnect' && event.type === 'connect') {
              return count + 1;
            }
            return count;
          }, 0) ?? 0;

          // Only show toast if there are actual flapping cycles
          // Live stream disconnects (0 cycles) are handled via server SSE
          if (cycles > 0) {
            showFlappingToast({
              encoderId: message.encoderID,
              simpleName: message.simpleName,
              cycles,
              severity: newState as 'warning' | 'alert',
            });
          }
        }
        break;
      }

      case 'batch':
        for (const update of message.updates) {
          processWorkerMessage(update);
        }
        break;
    }
  }, [updateJobFromWSS, updateEventStatus, processEncoderEvent]);

  // Keep ref updated with latest processWorkerMessage
  // This ensures the worker's onmessage handler always calls the latest version
  processWorkerMessageRef.current = processWorkerMessage;

  // Subscribe to rooms in batches
  const subscribeToBatch = useCallback(async (clientIDsToSubscribe: string[]) => {
    if (subscriptionInProgressRef.current) {
      return;
    }

    subscriptionInProgressRef.current = true;
    const total = clientIDsToSubscribe.length;
    let subscribed = 0;
    const { BATCH_SIZE, DELAY_BETWEEN_BATCHES_MS } = SCALEDRONE_CONFIG;

    for (let i = 0; i < clientIDsToSubscribe.length; i += BATCH_SIZE) {
      const batch = clientIDsToSubscribe.slice(i, i + BATCH_SIZE);

      for (const clientID of batch) {
        if (!roomsRef.current.has(clientID) && droneRef.current) {
          const room = droneRef.current.subscribe(clientID);
          roomsRef.current.set(clientID, room);

          room.on('open', (err: Error) => {
            if (err) {
              console.warn(`[Scaledrone] Room ${clientID} open error:`, err);
              return;
            }
            // console.log(`[Scaledrone] Room ${clientID} opened successfully`);
          });

          room.on('message', (message: unknown) => {
            if (workerRef.current) {
              workerRef.current.postMessage({ type: 'process', clientID, message });
            }
          });

          room.on('error', (err: Error) => {
            console.warn(`[Scaledrone] Room ${clientID} error:`, err);
          });

          subscribed++;
        }
      }

      setSubscriptionProgress({ subscribed, total });

      if (i + BATCH_SIZE < clientIDsToSubscribe.length) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
      }
    }

    // Clear progress after a short delay
    setTimeout(() => setSubscriptionProgress(null), 2000);
    subscriptionInProgressRef.current = false;
  }, []);

  // Fetch clients on auth
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    const loadClients = async () => {
      try {
        const token = await getAccessTokenSilently();
        await fetchClients(token, logout);
      } catch (err) {
        if (isLoginRequiredError(err)) {
          // Session expired - clear stale auth state and redirect to login.
          // returnTo prevents Auth0 falling back to the dev Allowed Logout URL.
          logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }
        console.warn('[ScaledroneProvider] Error fetching clients:', err);
      }
    };

    if (clients.length === 0) {
      loadClients();
    }
  }, [isLoading, isAuthenticated, clients.length, fetchClients, getAccessTokenSilently]);

  // Initialize Scaledrone connection (only once)
  useEffect(() => {
    const enabled = activeClientIDs.length > 0;
    let didInitialize = false;

    if (!enabled) {
      return;
    }

    // Safety checks
    if (droneRef.current) {
      const state = droneRef.current.readyState;
      if (state === 0 || state === 1) {
        return;
      }
    }

    if (isInitializingRef.current) {
      return;
    }

    if (circuitBreakerTrippedRef.current) {
      setStatus('error');
      setError('Circuit breaker tripped - connection disabled');
      return;
    }

    if (connectionAttemptsRef.current >= MAX_CONNECTION_ATTEMPTS) {
      circuitBreakerTrippedRef.current = true;
      setStatus('error');
      setError('Max connection attempts reached');
      return;
    }

    if (!SCALEDRONE_CHANNEL_ID) {
      setStatus('error');
      setError('Scaledrone channel ID not configured');
      return;
    }

    if (typeof window.Scaledrone === 'undefined') {
      setStatus('error');
      setError('Scaledrone SDK not loaded');
      return;
    }

    if (droneRef.current || workerRef.current) {
      return;
    }

    // Initialize
    didInitialize = true;
    hasInitializedRef.current = true;
    connectionAttemptsRef.current += 1;
    isInitializingRef.current = true;

    // Apply test mode limit
    const testClientIDs = SCALEDRONE_TEST_MODE_MAX_CLIENTS > 0
      ? activeClientIDs.slice(0, SCALEDRONE_TEST_MODE_MAX_CLIENTS)
      : activeClientIDs;

    // Create worker
    const worker = new Worker(
      new URL('../workers/scaledrone.worker.ts', import.meta.url)
    );
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<ScaledroneWorkerMessage>) => {
      // Use ref to always call the latest processWorkerMessage
      processWorkerMessageRef.current?.(event.data);
    };

    worker.onerror = (workerError) => {
      console.warn('[Scaledrone] Worker error:', workerError);
    };

    // Connection timeout
    connectionTimeoutRef.current = setTimeout(() => {
      if (!droneRef.current || droneRef.current.readyState !== 1) {
        circuitBreakerTrippedRef.current = true;
        isInitializingRef.current = false;
        setStatus('error');
        setError('Connection timeout');

        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
        if (droneRef.current) {
          droneRef.current.close();
          droneRef.current = null;
        }
      }
    }, CONNECTION_TIMEOUT_MS);

    setStatus('connecting');

    const drone = new window.Scaledrone(SCALEDRONE_CHANNEL_ID);
    droneRef.current = drone;

    drone.on('open', async (openError?: Error) => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      if (openError) {
        console.warn('[Scaledrone] Connection open error:', openError);
        isInitializingRef.current = false;

        if (connectionAttemptsRef.current >= CIRCUIT_BREAKER_THRESHOLD) {
          circuitBreakerTrippedRef.current = true;
        }

        setStatus('error');
        setError(openError.message);
        return;
      }

      console.log('[Scaledrone] Connection opened, subscribing to rooms...');
      isInitializingRef.current = false;
      setStatus('connected');

      const roomsToSubscribe = testClientIDs.filter(id => !roomsRef.current.has(id));
      if (roomsToSubscribe.length > 0) {
        await subscribeToBatch(roomsToSubscribe);
      }
    });

    drone.on('error', (droneError: Error) => {
      isInitializingRef.current = false;

      if (connectionAttemptsRef.current >= CIRCUIT_BREAKER_THRESHOLD) {
        circuitBreakerTrippedRef.current = true;
      }

      setStatus('error');
      setError(droneError.message);
    });

    drone.on('disconnect', () => {
      setStatus('disconnected');
    });

    drone.on('reconnect', async () => {
      setStatus('connected');

      const currentRooms = Array.from(roomsRef.current.keys());
      for (const clientID of testClientIDs) {
        if (!currentRooms.includes(clientID)) {
          // Re-subscribe logic would go here if needed
        }
      }
    });

    // Cleanup
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      if (droneRef.current) {
        const state = droneRef.current.readyState;
        if ((state === 0 || state === 1 || state === 2) && !intentionalCloseRef.current) {
          return;
        }
      }

      if (!didInitialize) {
        return;
      }

      intentionalCloseRef.current = true;

      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'flush' });
      }

      for (const [, room] of roomsRef.current) {
        try {
          room.unsubscribe();
        } catch (e) {
          console.warn('[Scaledrone] Error unsubscribing from room:', e);
        }
      }

      if (drone) {
        try {
          drone.close();
        } catch (e) {
          console.warn('[Scaledrone] Error closing connection:', e);
        }
      }

      if (workerRef.current) {
        try {
          workerRef.current.terminate();
        } catch (e) {
          console.warn('[Scaledrone] Error terminating worker:', e);
        }
      }

      workerRef.current = null;
      droneRef.current = null;
      roomsRef.current.clear();
      isInitializingRef.current = false;
      intentionalCloseRef.current = false;
      hasInitializedRef.current = false;
    };
  }, [activeClientIDs, processWorkerMessage, subscribeToBatch]);

  // Handle clientID changes (add/remove subscriptions)
  useEffect(() => {
    if (!droneRef.current || activeClientIDs.length === 0) return;

    const allowedClientIDs = SCALEDRONE_TEST_MODE_MAX_CLIENTS > 0
      ? activeClientIDs.slice(0, SCALEDRONE_TEST_MODE_MAX_CLIENTS)
      : activeClientIDs;

    const currentClients = new Set(roomsRef.current.keys());
    const newClients = new Set(allowedClientIDs);

    // Subscribe to new clients
    for (const clientID of newClients) {
      if (!currentClients.has(clientID)) {
        const room = droneRef.current.subscribe(clientID);
        roomsRef.current.set(clientID, room);

        room.on('open', (err: Error) => {
          if (err) {
            console.warn(`[Scaledrone] Room ${clientID} open error:`, err);
            return;
          }
          // console.log(`[Scaledrone] Room ${clientID} opened successfully`);
        });

        room.on('message', (message: unknown) => {
          if (workerRef.current) {
            workerRef.current.postMessage({ type: 'process', clientID, message });
          }
        });

        room.on('error', (err: Error) => {
          console.warn(`[Scaledrone] Room ${clientID} error:`, err);
        });
      }
    }

    // Unsubscribe from removed clients
    for (const clientID of currentClients) {
      if (!newClients.has(clientID)) {
        const room = roomsRef.current.get(clientID);
        if (room) {
          room.unsubscribe();
          roomsRef.current.delete(clientID);
        }
      }
    }
  }, [activeClientIDs]);

  const contextValue = useMemo(() => ({
    status,
    error,
    subscriptionProgress,
  }), [status, error, subscriptionProgress]);

  return (
    <ScaledroneContext.Provider value={contextValue}>
      {children}
    </ScaledroneContext.Provider>
  );
}
